/**
 * The canonical humanoid rig standard (rigVersion `humanoid-v1`).
 *
 * Rest convention (plan D4): every canonical bone has **identity local rotation** in rest.
 * Bone placement is expressed purely as local position offsets:
 *   localPosition(bone) = jointPosition(bone) - jointPosition(parent(bone))
 * Axes: Y up, character faces +Z, character-left is +X, arms out along ±X (T-pose).
 */

import {
  CANONICAL_BONE_IDS,
  REQUIRED_BONE_IDS,
  type CanonicalBoneId,
  type HierarchyValidationResult,
  type IkChain,
  type Vec3Tuple,
} from './types';

/** Parent of each canonical bone; `Root` has none. */
export const CANONICAL_PARENT: Record<CanonicalBoneId, CanonicalBoneId | null> = {
  Root: null,
  Hips: 'Root',
  Spine: 'Hips',
  Chest: 'Spine',
  Neck: 'Chest',
  Head: 'Neck',
  LeftUpperArm: 'Chest',
  LeftLowerArm: 'LeftUpperArm',
  LeftHand: 'LeftLowerArm',
  RightUpperArm: 'Chest',
  RightLowerArm: 'RightUpperArm',
  RightHand: 'RightLowerArm',
  LeftUpperLeg: 'Hips',
  LeftLowerLeg: 'LeftUpperLeg',
  LeftFoot: 'LeftLowerLeg',
  RightUpperLeg: 'Hips',
  RightLowerLeg: 'RightUpperLeg',
  RightFoot: 'RightLowerLeg',
};

function buildChildren(): Record<CanonicalBoneId, CanonicalBoneId[]> {
  const out = {} as Record<CanonicalBoneId, CanonicalBoneId[]>;
  for (const bone of CANONICAL_BONE_IDS) out[bone] = [];
  for (const bone of CANONICAL_BONE_IDS) {
    const parent = CANONICAL_PARENT[bone];
    if (parent) out[parent].push(bone);
  }
  return out;
}

/** Children of each canonical bone, in `CANONICAL_BONE_IDS` order. */
export const CANONICAL_CHILDREN: Record<CanonicalBoneId, readonly CanonicalBoneId[]> =
  buildChildren();

const CANONICAL_BONE_SET: ReadonlySet<string> = new Set<string>(CANONICAL_BONE_IDS);

export function isCanonicalBoneId(s: unknown): s is CanonicalBoneId {
  return typeof s === 'string' && CANONICAL_BONE_SET.has(s);
}

/** The four two-bone IK chains (plan D6). */
export const IK_CHAINS: readonly IkChain[] = [
  {
    name: 'leftArm',
    root: 'LeftUpperArm',
    mid: 'LeftLowerArm',
    end: 'LeftHand',
    side: 'left',
    limb: 'arm',
    fallbackBendDir: [0, 0, -1],
  },
  {
    name: 'rightArm',
    root: 'RightUpperArm',
    mid: 'RightLowerArm',
    end: 'RightHand',
    side: 'right',
    limb: 'arm',
    fallbackBendDir: [0, 0, -1],
  },
  {
    name: 'leftLeg',
    root: 'LeftUpperLeg',
    mid: 'LeftLowerLeg',
    end: 'LeftFoot',
    side: 'left',
    limb: 'leg',
    fallbackBendDir: [0, 0, 1],
  },
  {
    name: 'rightLeg',
    root: 'RightUpperLeg',
    mid: 'RightLowerLeg',
    end: 'RightFoot',
    side: 'right',
    limb: 'leg',
    fallbackBendDir: [0, 0, 1],
  },
];

/**
 * Validate a candidate parent map against the canonical hierarchy.
 * Reports missing bones, wrong parents and unknown bone ids. Never throws.
 *
 * `Root` is optional (see `REQUIRED_BONE_IDS`): imported rigs often have none. When `Root` is
 * absent, `Hips` may be a top-level bone (`null` parent) or still name `Root`. When `Root` is
 * present it must be the parent of `Hips`.
 */
export function validateCanonicalHierarchy(
  parentMap: Partial<Record<CanonicalBoneId, CanonicalBoneId | null>>,
): HierarchyValidationResult {
  const errors: string[] = [];

  for (const key of Object.keys(parentMap)) {
    if (!isCanonicalBoneId(key)) {
      errors.push(`Unknown bone id "${key}" is not part of the canonical rig.`);
    }
  }

  const hasRoot = 'Root' in parentMap;

  if (hasRoot && (parentMap.Root ?? null) !== null) {
    errors.push(`Bone "Root" must be the top of the hierarchy but has parent "${parentMap.Root}".`);
  }

  for (const bone of REQUIRED_BONE_IDS) {
    if (!(bone in parentMap)) {
      errors.push(`Missing required canonical bone "${bone}".`);
      continue;
    }
    const actual = parentMap[bone] ?? null;
    const expected = CANONICAL_PARENT[bone];

    if (bone === 'Hips') {
      const acceptable = hasRoot ? actual === 'Root' : actual === null || actual === 'Root';
      if (!acceptable) {
        errors.push(
          `Bone "Hips" has parent "${actual ?? 'null'}" but must be parented to ` +
            `${hasRoot ? '"Root"' : '"Root" or nothing'}.`,
        );
      }
      continue;
    }

    if (actual !== expected) {
      errors.push(
        `Bone "${bone}" has parent "${actual ?? 'null'}" but the canonical parent is ` +
          `"${expected ?? 'null'}".`,
      );
    }
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Rest-pose joint world positions of a ~1.8 m T-pose humanoid, used as the RIG-mode starting
 * estimate before it is scaled to the imported model's bounds.
 *
 * Elbows and knees are deliberately NOT collinear with their limb: elbows sit 3 cm toward -Z
 * (bending backwards) and knees 3 cm toward +Z (bending forwards), so a rest bend direction is
 * derivable for the two-bone IK solver instead of being degenerate.
 */
export const DEFAULT_JOINT_POSITIONS: Record<CanonicalBoneId, Vec3Tuple> = {
  Root: [0, 0, 0],
  Hips: [0, 0.95, 0],
  Spine: [0, 1.1, 0],
  Chest: [0, 1.28, 0],
  Neck: [0, 1.48, 0],
  Head: [0, 1.65, 0],

  LeftUpperArm: [0.18, 1.42, 0],
  LeftLowerArm: [0.49, 1.42, -0.03],
  LeftHand: [0.8, 1.42, 0],
  RightUpperArm: [-0.18, 1.42, 0],
  RightLowerArm: [-0.49, 1.42, -0.03],
  RightHand: [-0.8, 1.42, 0],

  LeftUpperLeg: [0.1, 0.9, 0],
  LeftLowerLeg: [0.1, 0.48, 0.03],
  LeftFoot: [0.1, 0.05, 0],
  RightUpperLeg: [-0.1, 0.9, 0],
  RightLowerLeg: [-0.1, 0.48, 0.03],
  RightFoot: [-0.1, 0.05, 0],
};

/**
 * The child that defines a bone's *direction* in rest. A bone points at exactly one child even
 * when it has several: `Hips` points up the spine (not down a leg), `Chest` up the neck (not out
 * an arm). Leaf bones (`Root`, `Head`, hands, feet) have no direction of their own — see
 * `computeRestAlignment` for how they are handled.
 */
export const CANONICAL_PRIMARY_CHILD: Partial<Record<CanonicalBoneId, CanonicalBoneId>> = {
  Hips: 'Spine',
  Spine: 'Chest',
  Chest: 'Neck',
  Neck: 'Head',
  LeftUpperArm: 'LeftLowerArm',
  LeftLowerArm: 'LeftHand',
  RightUpperArm: 'RightLowerArm',
  RightLowerArm: 'RightHand',
  LeftUpperLeg: 'LeftLowerLeg',
  LeftLowerLeg: 'LeftFoot',
  RightUpperLeg: 'RightLowerLeg',
  RightLowerLeg: 'RightFoot',
};

/** `[joint, limbRoot, limbEnd]` — joints that sit deliberately off their limb line. */
const PRE_BENT_JOINTS: readonly (readonly [CanonicalBoneId, CanonicalBoneId, CanonicalBoneId])[] = [
  ['LeftLowerArm', 'LeftUpperArm', 'LeftHand'],
  ['RightLowerArm', 'RightUpperArm', 'RightHand'],
  ['LeftLowerLeg', 'LeftUpperLeg', 'LeftFoot'],
  ['RightLowerLeg', 'RightUpperLeg', 'RightFoot'],
];

function straightenLimbs(
  positions: Record<CanonicalBoneId, Vec3Tuple>,
): Record<CanonicalBoneId, Vec3Tuple> {
  const out = {} as Record<CanonicalBoneId, Vec3Tuple>;
  for (const bone of CANONICAL_BONE_IDS) {
    const p = positions[bone];
    out[bone] = [p[0], p[1], p[2]];
  }

  for (const [joint, rootBone, endBone] of PRE_BENT_JOINTS) {
    const a = positions[rootBone];
    const b = positions[endBone];
    const p = positions[joint];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const dz = b[2] - a[2];
    const lengthSq = dx * dx + dy * dy + dz * dz;
    if (lengthSq < 1e-12) continue;
    const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy + (p[2] - a[2]) * dz) / lengthSq;
    out[joint] = [a[0] + dx * t, a[1] + dy * t, a[2] + dz * t];
  }

  return out;
}

/**
 * The reference rest pose that `restAlignment` measures every rig against — a **straight** T-pose.
 *
 * `DEFAULT_JOINT_POSITIONS` deliberately puts elbows 3 cm back and knees 3 cm forward so the
 * two-bone IK solver has a non-degenerate bend direction. That is an IK convenience, not a
 * statement about what "rest" means: using it as the alignment reference would bake a permanent
 * ~11 deg elbow and ~8 deg knee bend into every clip on every rig. Here the four pre-bent joints
 * are projected back onto their limb line, so a canonical identity pose is a genuine T-pose.
 */
export const REST_REFERENCE_POSITIONS: Record<CanonicalBoneId, Vec3Tuple> =
  straightenLimbs(DEFAULT_JOINT_POSITIONS);

/**
 * Local rest position of a bone given a joint-position map: own joint minus parent joint
 * (identity local rotation, per D4).
 */
export function localRestPosition(
  bone: CanonicalBoneId,
  positions: Record<CanonicalBoneId, Vec3Tuple> = DEFAULT_JOINT_POSITIONS,
): Vec3Tuple {
  const own = positions[bone];
  const parent = CANONICAL_PARENT[bone];
  if (!parent) return [own[0], own[1], own[2]];
  const p = positions[parent];
  return [own[0] - p[0], own[1] - p[1], own[2] - p[2]];
}

/** Canonical bones ordered parent-before-child (identical to `CANONICAL_BONE_IDS`). */
export function canonicalBonesInHierarchyOrder(): readonly CanonicalBoneId[] {
  return CANONICAL_BONE_IDS;
}
